import { useCallback, useEffect, useState } from 'react';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import useSnackbar from './useSnackbar';

interface ColumnPreferencesConfig {
  /** Preference key for the hidden columns list */
  hiddenKey: string;
  /** Preference key for the column order list */
  orderKey: string;
  /** Preference key for the column widths map */
  widthsKey: string;
  /** Preference key for the pinned columns map */
  pinnedKey: string;
  /** Default pinned columns (e.g. { left: ["date"] }) */
  defaultPinned?: GridPinnedColumnFields;
}

const useColumnPreferences = ({
  hiddenKey,
  orderKey,
  widthsKey,
  pinnedKey,
  defaultPinned = {},
}: ColumnPreferencesConfig) => {
  const { preferences, updatePreferences } = useUserPreferences();
  const apiRef = useGridApiRef();
  const snackbar = useSnackbar();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const [orderedColumnFields, setOrderedColumnFields] = useState<string[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    preferences[widthsKey] || {}
  );
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(
    preferences[pinnedKey] || defaultPinned
  );

  // Derive column visibility from preferences
  useEffect(() => {
    const hidden: string[] = preferences[hiddenKey] || [];
    const visibilityModel = hidden.reduce<GridColumnVisibilityModel>((m, f) => {
      m[f] = false;
      return m;
    }, {});
    setColumnVisibilityModel(visibilityModel);
  }, [preferences, hiddenKey]);

  // Sync column order from preferences
  useEffect(() => {
    setOrderedColumnFields(preferences[orderKey] || []);
  }, [preferences, orderKey]);

  // Handle column reorder events from DataGridPro
  const handleColumnOrderChange = useCallback(() => {
    if (apiRef.current) {
      const allCols = apiRef.current.getAllColumns();
      setOrderedColumnFields(allCols.map((c) => c.field));
    }
  }, [apiRef]);

  const handleColumnWidthChange = useCallback(
    (params: { colDef: { field: string }; width: number }) => {
      setColumnWidths((prev) => ({
        ...prev,
        [params.colDef.field]: params.width,
      }));
    },
    []
  );

  const handlePinnedColumnsChange = useCallback(
    (model: GridPinnedColumnFields) => {
      setPinnedColumns(model);
    },
    []
  );

  const handleSaveColumns = useCallback(async () => {
    try {
      const hiddenColumns = Object.keys(columnVisibilityModel).filter(
        (k) => !columnVisibilityModel[k]
      );
      await updatePreferences({
        [hiddenKey]: hiddenColumns,
        [orderKey]: orderedColumnFields,
        [widthsKey]: columnWidths,
        [pinnedKey]: pinnedColumns,
      });
      snackbar.showSnackbar(
        'Column preferences updated successfully!',
        'success'
      );
    } catch (err) {
      console.error('Error updating column preferences:', err);
      snackbar.showSnackbar(
        'Failed to save column preferences. Please try again.',
        'error'
      );
    }
  }, [
    columnVisibilityModel,
    orderedColumnFields,
    columnWidths,
    pinnedColumns,
    updatePreferences,
    snackbar,
    hiddenKey,
    orderKey,
    widthsKey,
    pinnedKey,
  ]);

  /** Apply saved column order to a column definitions array. */
  const applyColumnOrder = useCallback(
    (columns: GridColDef[]) => {
      if (orderedColumnFields.length === 0) return columns;
      const colMap = new Map(columns.map((c) => [c.field, c]));
      const ordered = orderedColumnFields
        .filter((f) => colMap.has(f))
        .map((f) => colMap.get(f)!);
      const orderedSet = new Set(orderedColumnFields);
      columns.forEach((c) => {
        if (!orderedSet.has(c.field)) ordered.push(c);
      });
      return ordered;
    },
    [orderedColumnFields]
  );

  /** Apply saved column widths to a column definitions array. */
  const applyColumnWidths = useCallback(
    (columns: GridColDef[]) => {
      return columns.map((col) =>
        columnWidths[col.field]
          ? { ...col, width: columnWidths[col.field] }
          : col
      );
    },
    [columnWidths]
  );

  /** Returns the DataGridPro props for column personalization. */
  const getColumnPrefsDataGridProps = useCallback(
    () => ({
      apiRef,
      columnVisibilityModel,
      onColumnVisibilityModelChange: setColumnVisibilityModel,
      onColumnOrderChange: handleColumnOrderChange,
      onColumnWidthChange: handleColumnWidthChange,
      pinnedColumns,
      onPinnedColumnsChange: handlePinnedColumnsChange,
    }),
    [
      apiRef,
      columnVisibilityModel,
      handleColumnOrderChange,
      handleColumnWidthChange,
      pinnedColumns,
      handlePinnedColumnsChange,
    ]
  );

  return {
    apiRef,
    snackbar,
    columnVisibilityModel,
    setColumnVisibilityModel,
    orderedColumnFields,
    columnWidths,
    pinnedColumns,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handlePinnedColumnsChange,
    handleSaveColumns,
    applyColumnOrder,
    applyColumnWidths,
    getColumnPrefsDataGridProps,
  };
};

export default useColumnPreferences;
